import { observable, action } from 'mobx';
import stores from 'cd-eft/models/Stores';
import { appConfig } from 'cd-eft/config/AppConfig';
import * as SETAPI from 'cd-eft/services/eftSetApi';
import MessageCenter from 'cd-eft/components/common/MessageCenter/MessageCenterVM';

const setStore = stores.AppStore.surveyEngineeringStore;
const designStore = stores.AppStore.designStore;
const surveyTypeSpredsheet = appConfig.hardCodes.surveyTypeSpredsheet; // 2
const surveyTypeCrossSlope = appConfig.hardCodes.surveyTypeCrossSlope; // 3

export class SOSurveysVM {
  @observable props;
  constructor(props: any) {
    this.props = props;
  }
  @observable notification: any = {
    message: '',
    header: '',
    type: '',
    hidden: true,
    timestamp: undefined,
  };
  @action.bound checkForProject() {
    if (!stores.AppStore.designStore.hasProject()) {
      this.notification = {
        message: designStore.noProjectMessage,
        header: 'Uh oh!',
        type: 'warn',
        hidden: false,
        timestamp: new Date(),
      };
    }
  }
}

export const preloadNewSurvey = () => {
  // if SO survey created, fill stationOffsetObject in Store
  if (setStore.currentSurvey.surveyType !== appConfig.hardCodes.surveyTypeXYZ) {
    setStore.resetStationOffsetObject();
    // calculate XY for beginning point of alignment
    if (setStore.surfaceCentroid) {
      setStore.stationOffsetObject.beginningX = setStore.surfaceCentroid.x;
      setStore.initialAlignmentStation.x = setStore.surfaceCentroid.x;
      setStore.initialOffsetStation.x = setStore.surfaceCentroid.x;

      setStore.stationOffsetObject.beginningY = setStore.surfaceCentroid.y;
      setStore.initialAlignmentStation.y = setStore.surfaceCentroid.y;
      setStore.initialOffsetStation.y = setStore.surfaceCentroid.y;
    }

    setStore.stationOffsetObject = {
      ...setStore.stationOffsetObject,
      surfaceDefId: designStore.currentSurface.surfaceDefId,
      surveyId: '',
      surveyName: setStore.currentSurvey.surveyName,
      alignmentName: `${setStore.currentSurvey.surveyName}_Centerline`,
      surfaceName: designStore.currentSurface.surfaceDefTitle,
      alignmentStations: [],
    };

    // fill ALIGNMENT stations
    setStore.stationOffsetObject.alignmentStations = [
      {
        ...setStore.initialAlignmentStation,
        x: setStore.stationOffsetObject.beginningX,
        y: setStore.stationOffsetObject.beginningY,
      },
    ];

    // fill OFFSET stations
    if (setStore.currentSurvey.surveyType === surveyTypeSpredsheet) {
      delete setStore.stationOffsetObject.crossSlopeStations; // deleting crossSlopeStations
      setStore.initialOffsetStation.alignmentName = setStore.stationOffsetObject.alignmentName;
      setStore.stationOffsetObject.offsetStations = [
        {
          ...setStore.initialOffsetStation,
          stationDistance: 0,
          hiElevation: 0,
          x: setStore.stationOffsetObject.beginningX,
          y: setStore.stationOffsetObject.beginningY,
          offsets: [...setStore.initialOffsetStation.offsets],
        },
      ];
    }

    // fill CROSSSLOPE stations
    if (setStore.currentSurvey.surveyType === surveyTypeCrossSlope) {
      delete setStore.stationOffsetObject.offsetStations; // deleting offsetStations
      setStore.initialCrossSlopeStation.alignmentName = setStore.stationOffsetObject.alignmentName;
      setStore.stationOffsetObject.crossSlopeStations = [
        {
          ...setStore.initialCrossSlopeStation,
          alignmentName: setStore.stationOffsetObject.alignmentName,
          crossSlopeStationOffsets: [...setStore.initialCrossSlopeStation.crossSlopeStationOffsets],
        },
      ];
    }
  }
};

export const preloadExistingSurvey = async (item) => {
  // ---------------------------- 2.Spreadsheet and 3.CrossSlope -------------------------------
  setStore.resetStationOffsetObject();
  const getSurveyAPI =
    item.surveyType === surveyTypeSpredsheet
      ? 'getStationOffsetSpreadsheetUsingGET'
      : 'getCrossSlopeSurveyByIdUsingGET';
  try {
    const surveyId = setStore.currentSurvey.surveyId;
    const response: any = await SETAPI.SetSurveyStationOffsetController[getSurveyAPI](surveyId);

    if (response) {
      setStore.currentSurvey = {
        ...setStore.currentSurvey,
        ...response.data,
      };
      const surveyName = setStore.currentSurvey.surveyName
        ? setStore.currentSurvey.surveyName
        : setStore.currentSurvey.alignment.alignmentName.replace('_Centerline', '');
      setStore.currentSurvey.surveyName = surveyName;
      setStore.stationOffsetObject = {
        ...setStore.stationOffsetObject,
        surfaceDefId: designStore.currentSurface.surfaceDefId,
        surveyId: setStore.currentSurvey.surveyId,
        surveyName: surveyName,
        alignmentName: setStore.currentSurvey.alignment.alignmentName,
        surfaceName: designStore.currentSurface.surfaceDefTitle,
        alignmentEntryMethod: setStore.currentSurvey.alignment.alignmentEntryMethod,
        alignmentAutoGrow: setStore.currentSurvey.alignment.alignmentAutoGrow,
        alignmentIncreasing: setStore.currentSurvey.alignment.alignmentIncreasing,
        alignmentAutoCreated: setStore.currentSurvey.alignment.alignmentAutoCreated,
        beginningX: setStore.currentSurvey.alignment.beginningX,
        beginningY: setStore.currentSurvey.alignment.beginningY,
      };
      if (response.data.alignment.alignmentStations && response.data.alignment.alignmentStations.length > 0) {
        setStore.stationOffsetObject.alignmentStations = [...response.data.alignment.alignmentStations];
      } else {
        setStore.stationOffsetObject.alignmentStations = [
          {
            ...setStore.initialAlignmentStation,
            x: setStore.currentSurvey.alignment.beginningX,
            y: setStore.currentSurvey.alignment.beginningY,
          },
          { ...setStore.initialAlignmentStation },
        ];
      }

      if (item.surveyType === 2) {
        // for offsetStations:
        delete setStore.stationOffsetObject.crossSlopeStations; // deleting crossSlopeStations
        if (response.data.offsetStations && response.data.offsetStations.length > 0) {
          setStore.stationOffsetObject.offsetStations = [...response.data.offsetStations];
          setStore.initialOffsetStation = {
            ...setStore.initialOffsetStation,
            alignmentName: setStore.stationOffsetObject.alignmentName,
            stationStep: setStore.stationOffsetObject.offsetStations[0].stationStep,
            entryMode: setStore.stationOffsetObject.offsetStations[0].entryMode,
            offsetType: setStore.stationOffsetObject.offsetStations[0].offsetType,
          };
        } else {
          setStore.stationOffsetObject.offsetStations = [
            {
              ...setStore.initialOffsetStation,
              offsets: [...setStore.initialOffsetStation.offsets],
              alignmentName: setStore.currentSurvey.alignment.alignmentName,
            },
          ];
        }
        // taking Z value for dependent alignment from offset stations
        if (setStore.stationOffsetObject.offsetStations) {
          setStore.stationOffsetObject.offsetStations.forEach(
            (el, i) =>
            (setStore.stationOffsetObject.alignmentStations[i].z =
              el.entryMode === 'ROD' ? el.hiElevation - el.offsets[1].z : el.offsets[1].z)
          );
        }
      }

      if (item.surveyType === 3) {
        // for crossSlopeStations:
        delete setStore.stationOffsetObject.offsetStations; // deleting offsetStations
        if (response.data.crossSlopes && response.data.crossSlopes.length > 0) {
          response.data.crossSlopes.map((cs, i) => {
            setStore.stationOffsetObject.crossSlopeStations[i] = {
              alignmentId: setStore.currentSurvey.alignment.alignmentId,
              alignmentName: setStore.stationOffsetObject.alignmentName,
              ...cs,
              crossSlopeStationOffsets: cs.crossSlopeStations,
            };
            delete setStore.stationOffsetObject.crossSlopeStations[i].crossSlopeStations;
            setStore.stationOffsetObject.crossSlopeStations[i].crossSlopeStationOffsets.forEach((el) => {
              delete el.crossSlopeId;
              delete el.crossSlopeStationId;
              delete el.offsets;
            });
          });
        } else {
          setStore.stationOffsetObject.crossSlopeStations = [
            {
              ...setStore.initialCrossSlopeStation,
              crossSlopeStationOffsets: [...setStore.initialCrossSlopeStation.crossSlopeStationOffsets],
              alignmentId: setStore.currentSurvey.alignment.alignmentId,
              alignmentName: setStore.currentSurvey.alignment.alignmentName,
            },
          ];
        }
        // taking Z value for dependent alignment from offset stations
        setStore.stationOffsetObject.crossSlopeStations.forEach(
          (el, i) =>
          (setStore.stationOffsetObject.alignmentStations[i].z =
            el.offsetType === 'TYPE_ROD'
              ? el.hiElevation - el.crossSlopeStationOffsets[0].z
              : el.crossSlopeStationOffsets[0].z)
        );
      }
    }
  } catch (error) {
    MessageCenter.addError((error as Error).message);
  } finally {
    setStore.currentTab = 'Edit Survey';
    setStore.isLoading = false;
  }
};
